import {
  chain,
  groupBy,
  find,
  curry,
  pipe,
  reduce,
  prop,
  map,
  propEq,
  filter,
  innerJoin,
  head,
  tail,
  converge,
  length,
  always,
  values,
  identity,
  max,
  fromPairs,
  mergeRight,
  sum,
  both,
  isEmpty,
} from 'ramda'
import { sumProp, makeObj } from '@v/util/briqRamdaExt.js'
import DealProductType from '@v/bookings/edit/models/DealProductType.js'

const fromDeal = p => !!p.ProductGroupKey
const notExtra = p => p.DealProductType !== DealProductType.CrossSell

export const getBookingDeals = products =>
  pipe(
    filter(both(fromDeal, notExtra)),
    groupBy(prop('ProductGroupKey')),
    Object.entries,
    map(([key, bookingProducts]) => ({
      dealKey: key,
      dealName: bookingProducts[0].ProductGroupName,
      products: bookingProducts,
      scheduledProducts: bookingProducts.filter(p => p.NeedsToBeScheduled),
      nonScheduledProducts: bookingProducts.filter(p => !p.NeedsToBeScheduled),
    })),
    map(bookingDeal => {
      const quantities = getDealQuantities(bookingDeal, products)
      const isSegmentless = isEmpty(quantities.perSegment)
      return {
        ...bookingDeal,
        quantities,
        isSegmentless,
      }
    }),
  )

// get allowed segments for bookingdeal,
// try to determine from first nonsplit product
// otherwise sum from split products

export function getDealQuantities(bookingDeal, products) {
  const allowedSegments = getAllowedSegmentsForBookingDeal(bookingDeal, products)
  return allowedSegments.length
    ? getSegmentfulDealQuantities(bookingDeal, allowedSegments)
    : getSegmentlessDealQuantities(bookingDeal)
}

function getSegmentfulDealQuantities(bookingDeal, allowedSegments) {
  const firstNonSplitProduct = getFirstNonSplitBookingProduct(bookingDeal)
  const base = pipe(
    map(segment => [segment, 0]),
    fromPairs,
  )(allowedSegments)
  if (firstNonSplitProduct) {
    const perSegment = mapQuantitiesPerSegment(firstNonSplitProduct)
    return {
      total: pipe(values, sum)(perSegment),
      perSegment: mergeRight(base, perSegment),
      segmentless: 0,
    }
  } else {
    const perSegment = sumSplitSegmentfulQuantities(bookingDeal.products)
    return {
      total: pipe(values, sum)(perSegment),
      perSegment: mergeRight(base, perSegment),
      segmentless: 0,
    }
  }
}

function mapQuantitiesPerSegment(bookingProduct) {
  const divisor = bookingProduct.QuantityInProductGroup || 1
  return pipe(
    prop('QuantitiesPerClassification'),
    chain(prop('QuantitiesPerSegment')),
    groupBy(prop('SegmentCode')),
    map(pipe(head, prop('Quantity'), x => x / divisor)),
  )(bookingProduct)
}

export const sumSegmentQuantities = pipe(
  chain(prop('QuantitiesPerClassification')),
  chain(prop('QuantitiesPerSegment')),
  groupBy(prop('SegmentCode')),
  map(sumProp('Quantity')),
)

const sumSplitSegmentfulQuantities = pipe(
  groupBy(prop('ProductGroupProductKey')),
  values,
  head,
  sumSegmentQuantities,
)

function getSegmentlessDealQuantities(bookingDeal) {
  const firstNonSplitProduct = getFirstNonSplitBookingProduct(bookingDeal)
  const divisor = firstNonSplitProduct?.QuantityInProductGroup || 1
  return firstNonSplitProduct
    ? {
        total: firstNonSplitProduct.ProductQuantity,
        perSegment: {},
        segmentless: firstNonSplitProduct.ProductQuantity / divisor,
      }
    : sumSplitSegmentlessQuantities(bookingDeal.products)
}

const sumSplitSegmentlessQuantities = pipe(
  groupBy(prop('ProductGroupProductKey')),
  map(sumProp('ProductQuantity')),
  values,
  reduce(max, 0),
  makeObj({
    total: identity,
    segmentless: identity,
    perSegment: always({}),
  }),
)

function getFirstNonSplitBookingProduct(bookingDeal) {
  return find(
    bp => !bookingProductIsSplit(bookingDeal, bp.ProductGroupProductKey),
    bookingDeal.products,
  )
}

// A split booking product means that quantities of deal are distributed over multiple instances of the product on the deal
// The multiple instances of the product are identified by bookingproducts that have the same ProductGroupProductKey (the same junction between deal and product)
export const bookingProductIsSplit = curry((bookingDeal, productGroupProductKey) =>
  pipe(
    prop('products'),
    reduce((a, n) => (n.ProductGroupProductKey === productGroupProductKey ? a + 1 : a), 0),
    x => x > 1,
  )(bookingDeal),
)

// takes the first element as the starting point for reduce
const reduce2 = curry((f, a) => converge(reduce(f), [head, tail])(a))

export function getAllowedSegmentsForBookingDeal(bookingDeal, products) {
  const productSegments = pipe(
    map(getActiveSegments(products)),
    filter(length),
  )(bookingDeal.products)
  return !productSegments.length
    ? []
    : pipe(
        reduce2((a, n) => innerJoin((s1, s2) => s1.SegmentCode === s2.SegmentCode, a, n)),
        map(prop('SegmentCode')),
      )(productSegments)
}

const getActiveSegments = products => pipe(findProduct(products), prop('ActiveSegments'))

const findProduct = products => bookingProduct =>
  find(propEq('Key', bookingProduct.ProductKey), products)

export function getQuantitiesPerSegmentForSplitProduct(bookingDeal, bookingProduct) {
  return pipe(
    groupBy(prop('ProductGroupProductKey')),
    prop(bookingProduct.ProductGroupProductKey),
    chain(prop('QuantitiesPerClassification')),
    chain(prop('QuantitiesPerSegment')),
    groupBy(prop('SegmentCode')),
    map(sumProp('Quantity')),
  )(bookingDeal.products)
}
